import React from 'react'
import { NextSeo } from 'next-seo'
import { ExternalLink } from '@/atoms/ExternalLink'
import { InternalLink } from '@/atoms/InternalLink'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { paths } from '@/constants/paths'
import { LegalLayout } from '@/layouts/LegalLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { ManageCookieConsentLink } from '@/molecules/CookieManagement'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'

const ArchivedPrivacyPage: NextPageWithLayout = () => {
  return (
    <>
      <NextSeo nofollow noindex />
      <Privacy_2024_08_12 />
    </>
  )
}

ArchivedPrivacyPage.getLayout = (page) => {
  return <LegalLayout>{page}</LegalLayout>
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/legal/privacy/2024-08-12.tsx' },
  async ({ locale }) => {
    return {
      props: {
        ...(await loadTranslations(locale, ['common', 'account'])),
      },
      revalidate: false,
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default ArchivedPrivacyPage

/**
 *
 * Hey, are you about to make changes to this file? Read this!!!
 *
 * When the Privacy Policy gets an update, the following process needs to be followed:
 *   - Copy this file and make a new file.
 *   - Name the new file after the date of the udpate using the pattern YYYY-MM-DD.
 *   - Make your changes in the new file. Don't change this copy! It's an official archive!
 *   - Make sure to update the "Updated:" date in the new file.
 *   - Update the name of the exported component in the new file to Privacy_YYYY_MM_DD
 *   - Import and use the new component in the "evergreen" file: ./index.tsx
 *
 * That should be it for the code changes. After you have deployed and verified that the new page
 * is up in production and that the evergreen page is using the new version, then
 * you'll need to make changes in the auth service.
 *   - Go to https://auth.angel.com/admin/document
 *     - You will need an admin account for Ellis Island to access this. If you do not have access,
 *       then ask for help getting access in Slack, currently the #platform-dev channel.
 *   - Find the latest "Privacy Policy" document for angel.com and take note of the version number.
 *     Note that there may be other "Privacy Policy" documents for other websites, e.g. invest.angel.com.
 *   - Create a new "Document" using the "Add New Document" button.
 *   - The "Title" will be visible to the end user, so the title should be "Privacy Policy".
 *   - Set the "Source" to the fully qualified url for the newly deployed terms page.
 *     For example, https://www.angel.com/legal/privacy/2023-05-15
 *     It is important to use the full url, because it will get stored as the document which was signed.
 *     Do not use the evergreen url as the source.
 *   - Set the "Version" to one number higher than the previous document.
 *   - Check the "Published" boolean.
 *   - Click the "Create" button.
 *
 * Now that you've created a new document, you need to make it required in the sign in flow.
 *   - Go to https://auth.angel.com/admin/client
 *   - Find the "Angel Web" client application and click on it.
 *   - In the "Required Documents" section, de-select the old document and select the new one.
 *   - Scroll to the bottom of the page and click "Save Changes"
 *
 * Everything should be wired up now. The UpdatedLegalDocuments modal should appear as soon as you've
 * completed the above. You can also verify that it's working by signing out and then sign in again
 * on angel.com. When you sign in, you should be required to accept the new document before signing in.
 *
 * After verifying that the document has been updated and the flow is working for web, broadly publicize
 * the change. The other apps that rely on the web document will need to update their client applications
 * as well in Ellis Island to require the new version of the document in their sign in flows.
 *
 */

const orderedListRoman = 'list-[lower-roman] list-inside ml-4'
const orderedListAlpha = 'list-[lower-alpha] list-inside ml-4'

export const Privacy_2024_08_12: React.FC = () => {
  return (
    <div className="bg-white py-20 text-justify text-black">
      <PaddedContainer>
        <div className="flex flex-col items-center gap-4 p-10">
          <h1 className="text-center text-[1.3rem] font-bold md:text-[2rem]">
            <p data-testid="company">Angel Studios</p>
            <p data-testid="privacy-policy-header">Privacy Policy</p>
          </h1>
          <p>Updated: August 12, 2024</p>
          <p>
            Welcome to Angel Studios! Angel Studios, Inc. (“Angel Studios” or “we”, “us” or “our”) respect your privacy.
            This Privacy Policy (“Policy”) describes how we collect, use, protect and disclose Personal Data in
            connection with your use of our websites including, without limitation,{' '}
            <InternalLink data-testid="home-link" className="underline" href={paths.home}>
              {paths.getBaseURL()}
            </InternalLink>
            ,{' '}
            <InternalLink className="underline" href={paths.collectiblesMarketplace.index}>
              {paths.collectiblesMarketplace.index}
            </InternalLink>
            , and any other website that includes a link to this Privacy Policy, along with the features,
            functionalities, applications, browser extensions and other services available through those websites
            (collectively, our “Websites”); your use of our mobile applications distributed on our Websites and the
            Google Play Store and Apple App Store, including, without limitation, Angel Studios, Angel Marketplace, The
            Chosen, Dry Bar Comedy, and any other mobile application that includes a link to this Privacy Policy, along
            with all features, functionalities, applications, browser extensions and other services available through
            our mobile applications (collectively, our “Apps”); and personal information we obtain from any third party
            site connected to our Websites or our Apps or where we post content or invite your feedback or
            participation, including, without limitation, Facebook, Twitter, YouTube, and LinkedIn (collectively, “Third
            Party Sites” and, together with our Websites and Apps, our “Platform”). This Policy also applies to any
            personal information we collect or receive in connection with any other interactions we have with you
            outside of our Platform in connection with our business operations (our “Operations”). “NFT” in this Policy
            means a non-fungible token or similar digital item implemented on a blockchain (such as the Ethereum
            blockchain or Angel’s Sidechain), which uses smart contracts to link to or otherwise be associated with
            certain content or data.
          </p>
          <p>
            PLEASE NOTE, THIS POLICY DOES NOT APPLY TO THE COLLECTION, USE, PROTECTION, OR DISCLOSURE OF PERSONAL DATA
            BY OUR AFFILIATE, VAS PORTAL, LLC (“ANGEL FUNDING”), INCLUDING, WITHOUT LIMITATION, THROUGH OR IN CONNECTION
            WITH THE ANGEL FUNDING WEBSITE, LOCATED AT{' '}
            <ExternalLink data-testid="invest-link" className="underline" href={paths.external.invest.index}>
              {paths.external.invest.index}
            </ExternalLink>
            , OR ANGEL FUNDING’S OPERATIONS. ANGEL FUNDING’S PRACTICES WITH RESPECT TO THE PERSONAL DATA IT COLLECTS ARE
            PROVIDED IN THE ANGEL FUNDING PRIVACY POLICY, AVAILABLE AT{' '}
            <ExternalLink data-testid="invest-privacy-link" className="underline" href={paths.external.invest.privacy}>
              {paths.external.invest.privacy}
            </ExternalLink>
            .
          </p>
          <p>
            You can access and interact with our Platform in many ways, including from a computer or mobile device. This
            Policy applies regardless of how you access or interact with our Platform. Depending on how you interact
            with our Platform, there may be some cases where we need to provide you with additional information about
            how we collect, use, protect or disclose your Personal Data and/or what options you have regarding our
            collection, use or disclosure of your Personal Data. We may provide this additional information through
            notices on our Platform or via direct communications with you. If we do this, unless we tell you otherwise,
            the additional information provided in those notices will apply in addition to what is described in this
            Policy.
          </p>
          <p className="font-semibold">
            Please read this Policy carefully. If you do not want us to collect, use or disclose your Personal Data in
            the ways described in this Policy, please do not use or otherwise interact with our Platform, provide us
            with your Personal Data or authorize any third party to make your Personal Data available to us. By using or
            interacting with our Platform, you acknowledge that you understand that we may use your Personal Data as
            described in this Policy. Please note, as described in this Policy, we may receive your Personal Data from
            third parties that have not expressly told you that they will provide your Personal Data to us. If you have
            any questions about how we collect, use, protect or disclose your Personal Data in connection with our
            Platform, please contact us by email at{' '}
            <a data-testid="support-link" className="underline" href="mailto:privacy@angel.com">
              privacy@angel.com
            </a>
            .
          </p>

          <ol className="list-inside list-decimal">
            <li>
              <span className="font-semibold">PERSONAL DATA AND OTHER INFORMATION WE COLLECT</span>
              <p>
                Our primary goals in collecting Personal Data are to provide and improve our Platform, conduct our
                Operations, communicate with people and enable visitors to our Platform to easily navigate and enjoy
                them.
              </p>
              <p className="mt-4">
                When we refer to “Personal Data” in this Policy, we mean information that identifies, relates to,
                describes or is reasonably capable of being associated with, or could reasonably be linked, directly or
                indirectly, to a specific person. This might be information that directly identifies someone, such as
                their name, or it may be information that identifies someone only when combined with other information
                available to us, such as a person’s activity on our Platform when associated with their username. Please
                note, except as provided under applicable laws, Personal Data does not include information that is
                publicly available through government records or information that has been de-identified or aggregated
                in a way that it cannot be used to identify a specific individual.
              </p>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Categories of Personal Data We Collect</span> Depending on how you
                  access, use or otherwise interact with our Platform, we, or third parties acting on our behalf, may
                  collect the following categories of Personal Data about you:
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      Identifiers and contact information, such as your real name, alias, email address, telephone
                      number, postal address, physical address, unique personal identifier, government-issued
                      identification number, online identifier, IP address and other similar information;
                    </li>
                    <li className="mt-4">
                      Demographic information, such as your age, gender, ethnicity, national origin and other similar
                      information;
                    </li>
                    <li className="mt-4">
                      Financial information, such as your bank account number, credit card number, debit card number,
                      cryptocurrency wallet information and other similar information;
                    </li>
                    <li className="mt-4">
                      Transactional information, such as information about purchases you have made or participated in
                      through our Platform or Operations and other similar information;
                    </li>
                    <li className="mt-4">
                      Internet and other similar network activity information, such as your browsing history, search
                      history, interactions with the online portions of our Platform and online advertisements and other
                      similar information;
                    </li>
                    <li className="mt-4">
                      Communication information, such as information contained in voice mails, chats, emails and other
                      similar information;
                    </li>
                    <li className="mt-4">
                      Location information, such as where you live, where you are located and other similar information;
                    </li>
                    <li className="mt-4">
                      Preference information, such as your interests, hobbies, opinions and other similar information;
                      and
                    </li>
                    <li className="mt-4">Other information you provide to us.</li>
                  </ol>
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Other Information We Collect</span> We, or third parties acting on our
                  behalf, collect certain information about the device you use when you visit or otherwise interact with
                  our Platform and through the use of cookies, pixels and other web technologies (collectively, “Usage
                  Data”). Usage Data, by itself, generally does not identify the specific person using the device that
                  generated it. This means that Usage Data usually will not be considered Personal Data and our use of
                  such Usage Data is not subject to this Policy unless required pursuant to applicable law. However, if
                  we link Usage Data directly with other Personal Data or other information that could identify you, we
                  will treat the linked Usage Data as Personal Data and use the linked Usage Data as described in this
                  Policy.
                  <p className="mt-4">
                    Depending on how you interact with our Platform, the Usage Data that we, or third parties acting on
                    our behalf, may collect may include:
                  </p>
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      Device information, such as the IP address, general geographic location, operating system, browser
                      type, language preferences, mobile phone number, device identifier and other similar information
                      associated with a device used to access, use or interact with our Platform;
                    </li>
                    <li className="mt-4">
                      Information about online interactions with our Platform, such as links that are clicked, pages or
                      screens that are viewed, access and session times, interactions with emails we send and other
                      similar information associated with a device used to access, use or interact with our Platform;
                    </li>
                    <li className="mt-4">
                      Information about internet and network activity, such as websites visited, search engine results
                      and similar information associated with a device that accesses or interacts with our Platform
                      online;
                    </li>
                    <li className="mt-4">
                      Other information automatically collected by cookies and other technologies on our Platform;
                    </li>
                    <li className="mt-4">
                      Any other information that a browser or device automatically sends whenever connected to a website
                      or an online application; and
                    </li>
                    <li className="mt-4">
                      Data from activity that is publicly visible and/or accessible on blockchains, including but not
                      limited to, blockchain addresses and information regarding purchases, sales, or transfers of NFTs,
                      which may then be associated with other data you have provided to us.
                    </li>
                    <li className="mt-4">
                      Information on video or other content views and interactions, including but not limited to, video
                      name, show name, episode number, as further outlined in our{' '}
                      <InternalLink className="underline" href={paths.legal.videoPrivacyPolicy}>
                        Video Privacy Policy
                      </InternalLink>
                      .
                    </li>
                  </ol>
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">HOW WE COLLECT PERSONAL DATA</span>
              <p className="mt-4">
                A large portion of the Personal Data we, or third parties acting on our behalf, collect is directly from
                you and other users who provide their Personal Data to us directly. In some cases, we may collect
                Personal Data from third parties in the ways described in this Policy.
              </p>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Directly from You</span> When you access, use or otherwise interact
                  with our Platform, or otherwise engage with us in connection with our Platform, such as requesting
                  more information about us, communicating with us, accessing, using or otherwise interacting with our
                  Platform, we may ask you to provide certain Personal Data. Some of the Personal Data we request with
                  respect to certain features of our Platform may not be required; however, we will let you know if
                  specific Personal Data is required to use those features. If you do not provide us with the Personal
                  Data required to use a certain feature on our Platform, you will not be able to use that feature,
                  especially those features where we are legally required to collect the Personal Data and those where
                  it is impossible for the feature to operate without the required Personal Data (e.g., collecting your
                  payment information in order to process an order you place in the Angel store).
                </li>
                <li className="mt-4">
                  <span className="font-semibold">From Third Parties</span> We, or third parties acting on our behalf,
                  may collect Personal Data about you from third parties, including, without limitation, online and
                  offline sources, entities affiliated with us, our service providers, social media platforms,
                  advertising networks and other public or commercial sources (collectively, “Third Party Sources”). The
                  collection of Personal Data from these Third Party Sources can occur in a variety of ways, including,
                  without limitation:
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      By sharing our content via Third Party Sites or accessing, using or interacting with our Platform
                      through Third Party Sites, you acknowledge that we may collect, share, store and use any Personal
                      Data we receive from those Third Party Sites about you. In some cases, you may be able to revoke
                      our access to your Personal Data and other content and information we receive from Third Party
                      Sites by changing the appropriate settings for your account with the applicable Third Party Site.
                    </li>
                    <li className="mt-4">
                      Some of the features of our Platform may provide you with the ability to register and sign into
                      our Platform through the accounts you have with certain third parties, such as Facebook, Google,
                      or Apple (“Third Party Single Sign On Providers”). When you use these features, we, or third
                      parties acting on our behalf, gather the data necessary to create an account on our Platform from
                      the applicable Third Party Single Sign On Provider, which may include, without limitation, your
                      name, email address, IP address, device model, device operating system and web browser, time zone,
                      carrier, screen size, processor cores, total disk space and remaining disk space. Your data
                      associated with the applicable Third Party Single Sign On Provider will not be sold, redisclosed
                      or edited through this process. Your ability to use this option may be limited where the
                      applicable Third Party Single Sign On Provider requires you to use a device or operating system
                      that meets that Third Party Single Sign On Provider’s requirements. Please note, by using these
                      features, the applicable Third Party Single Sign On Provider will know that you are signing into
                      our Platform and that you are a user of our Platform.
                    </li>
                    <li className="mt-4">
                      We, or third parties acting on our behalf, may receive Personal Data about you from third parties
                      that provide services to us through or in connection with the operation of our Platform and our
                      Operations (“Operational Service Providers”). Operational Service Providers may include, without
                      limitation, hosting providers and payment providers.
                    </li>
                    <li className="mt-4">
                      We, or third parties acting on our behalf, may receive Personal Data about you from our
                      Affiliates. This Personal Data may include, without limitation, information related to your
                      access, use or interaction with our Affiliates and/or the services, products and other things they
                      provide.
                    </li>
                    <li className="mt-4">
                      We, or third parties acting on our behalf, may also receive Personal Data about you from other
                      third parties, such as other users of our Platform and those that refer you to our Platform.
                    </li>
                    <li className="mt-4">
                      We may receive Personal Data about you from companies that offer their products and/or services
                      for use in conjunction with our Platforms or whose products and/or services may be linked from our
                      Platforms. For example, third-party wallet providers provide us with your blockchain address and
                      certain other information you choose to share with those wallet providers. We may add this to the
                      data we have already collected from or about you through our Platform.
                    </li>
                  </ol>
                  <p className="mt-4">
                    Please note, we may combine the Personal Data and other content and information we receive about you
                    from multiple sources, including, without limitation, Personal Data we collect directly from you and
                    one or more Third Party Sources.
                  </p>
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">HOW WE COLLECT USAGE DATA</span>
              <p className="mt-4">
                We, and third parties acting on our behalf, collect Usage Data in a variety of ways. Some Usage Data,
                such as the IP address of a device attempting to access, use or interact with our Platform, is necessary
                for us to make certain portions of our Platform available. Other Usage Data, such as information about
                which features of our Platform are popular, helps us improve and advertise our Platform. This section
                describes the primary ways Usage Data is collected in connection with our Platform.
              </p>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  The systems we use to make our Platform available to you. When you access, use or interact with our
                  Platform, these systems automatically record certain information that your browser or device sends
                  whenever you visit a website or utilize an online application, such as information about how long your
                  device is connected to our Platform and the activities that device engages in while connected.
                </li>
                <li className="mt-4">
                  Cookies, which are small data files that are uploaded to the hard drive or other storage location of
                  devices that access, use or interact with our Platform. Among other things, cookies help us improve
                  our Platform and our users’ experience. We use cookies to see which areas and features are popular and
                  to understand usage of our Platform. We use first party cookies, served directly by us to the devices
                  that access our Platform, to recognize devices when they revisit our Platform. We also use third party
                  cookies, which are cookies that are placed by other parties we work with. These parties may upload
                  unique cookies to your device to collect information about your use of our Platform and potentially
                  other online activities you engage in over time across different websites and other online
                  applications. The Options/Settings section of most internet browsers will tell you how to manage
                  cookies and other technologies that may be uploaded to your device, including how to disable such
                  technologies. You can <ManageCookieConsentLink>Manage Cookie Preferences</ManageCookieConsentLink>{' '}
                  here, or you can disable our cookies, or all cookies, through your browser settings, but please note,
                  disabling cookies may impact some of the features of our Platform and prevent them from operating as
                  intended. You can learn more about cookies, including how to see what cookies have been uploaded to
                  your device and how to manage and delete them, by visiting{' '}
                  <ExternalLink
                    data-testid="all-about-cookies-link"
                    className="underline"
                    href="https://www.allaboutcookies.org/"
                  >
                    https://www.allaboutcookies.org/
                  </ExternalLink>
                  .
                </li>
                <li className="mt-4">
                  Other tracking technologies, such as web beacons, clear gifs, and pixel tags. These technologies may
                  be used on or in connection with our Platform, including, without limitation, in emails we send. We
                  may use these technologies to deliver cookies, count visits, understand usage and observe data on
                  email delivery, open rates, click rates, bounces, unsubscribes and other information.
                </li>
                <li className="mt-4">
                  Third party analytic tools, such as:
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      Google Analytics, a web analytics tool that assists us in understanding how our Platform is used.
                      Google Analytics will place cookies on your device that will generate information we select about
                      your use of our Platform, including your device’s IP address. That information will be transmitted
                      directly to and stored by Google. The information will be used for the purposes of evaluating use
                      of our Platform, compiling reports on activity on our Platform for our use and providing other
                      services relating to activity on, and usage of, our Platform. Google may also transfer this
                      information to third parties where required to do so by law, or where such third parties process
                      the information on Google’s behalf. You may refuse the use of cookies by selecting the appropriate
                      settings on your browser. Please note that by doing so, you may not be able to use the full
                      functionality of our Platform. Google’s practices with respect to the Personal Data collected
                      through Google Analytics is covered by Google’s privacy policy, available at{' '}
                      <ExternalLink
                        data-testid="google-privacy-link"
                        className="underline"
                        href="https://policies.google.com/privacy"
                      >
                        https://policies.google.com/privacy
                      </ExternalLink>
                      . Google allows you to opt out of Google Analytics. You may opt-out by visiting{' '}
                      <ExternalLink
                        data-testid="google-ads-link"
                        className="underline"
                        href="https://tools.google.com/dlpage/gaoptout"
                      >
                        https://tools.google.com/dlpage/gaoptout
                      </ExternalLink>
                      .
                    </li>
                    <li className="mt-4">
                      Facebook Pixel, a tool that allows us to measure the effectiveness of our Facebook advertising
                      campaigns, uses a cookie placed by Facebook. The information collected by that cookie will be
                      transmitted to and stored by Facebook. For more information about Facebook’s privacy practices,
                      please refer to{' '}
                      <ExternalLink
                        data-testid="facebook-privacy-link"
                        className="underline"
                        href="https://www.facebook.com/about/privacy/"
                      >
                        https://www.facebook.com/about/privacy/
                      </ExternalLink>
                      . To opt-out, please see{' '}
                      <ExternalLink
                        data-testid="facebook-ads-link"
                        className="underline"
                        href="https://www.facebook.com/ads/preferences"
                      >
                        https://www.facebook.com/ads/preferences
                      </ExternalLink>
                      .
                    </li>
                  </ol>
                  <p className="mt-4">
                    Please note, we have not yet developed a response to browser “Do Not Track” signals and do not
                    change any of our data collection practices when we receive such signals. We will continue to
                    evaluate potential responses to “Do Not Track” signals in light of industry developments or legal
                    changes.
                  </p>
                </li>
                <li className="mt-4">
                  Third-Party Wallets. To use our Platform, you must use a third-party wallet which allows you to engage
                  in transactions on public blockchains. Your interactions with any third-party wallet provider are
                  governed by the applicable terms of service and privacy policy of that third party.
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">HOW WE USE PERSONAL DATA</span>
              <p className="mt-4">
                We use Personal Data for various purposes to operate and improve our Platform and to conduct our
                Operations. The primary purposes for which we use Personal Data are:
              </p>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  Marketing Purposes – we may, either on our own or jointly with our third party marketing partners
                  (“Marketing Partners”), use the Personal Data we, or third parties acting on our behalf, collect for
                  marketing purposes, including notifying you of special promotions, opportunities, offers and events
                  via push notifications, e-mail and other means, all subject to our compliance with applicable laws
                  (“Marketing Purposes”). We may also link Personal Data (including your name, mobile phone number, and
                  email address) with other information (including information we automatically collect as described in
                  this Policy) and use such linked information for Marketing Purposes. Our Marketing Purposes may
                  include using Personal Data collected through cookies and other tracking technologies to facilitate
                  the delivery of targeted ads, promotions, and offers to you, on behalf of ourselves and our Marketing
                  Partners on and off our Platform. If you do not want us to use your Personal Data for marketing
                  purposes, you may opt-out in accordance with the “HOW TO MANAGE YOUR PERSONAL DATA” section below.
                </li>
                <li className="mt-4">
                  Non-Marketing Purposes – we may use the Personal Data we, or third parties acting on our behalf,
                  collect for non-marketing purposes, including: (1) operating and improving our Platform; (2)
                  validating your identity; (3) carrying out our obligations and enforcing our rights arising from any
                  contracts we have entered into, including, without limitation, any contracts between you and us; (4)
                  sending you emails to provide you with alerts and updates about your engagement with our Platform; (5)
                  conducting statistical or demographic analysis; (6) complying with legal and regulatory requirements;
                  (7) customizing your experience with our Platform; (8) protecting and defending Angel Studios against
                  legal actions or claims; (9) preventing fraud; (10) satisfying contractual obligations; (11)
                  cooperating with law enforcement or other governmental agencies for purposes of investigations,
                  national security, public safety or matters of public importance when we are legally required to do so
                  or believe that disclosure of Personal Data is necessary or appropriate to protect the public
                  interest; (12) those you authorize or instruct us to do; and (13) other business purposes permitted
                  under applicable law.
                </li>
              </ol>

              <p className="mt-4">
                We will not collect additional categories of Personal Data or use the Personal Data we, or third parties
                acting on our behalf, collect in connection with our Platform for materially different, unrelated or
                incompatible purposes without providing you notice.
              </p>
              <p className="mt-4">
                Please note, we may use and disclose aggregated, anonymized or de-identified information collected in
                connection with our Platform and other information that does not identify any individual (“Anonymized
                Data”) for any lawful purposes, which may include, without limitation, purposes not described in this
                Policy. Except as otherwise required under applicable laws, our use and disclosure of such Anonymized
                Data is not subject to this Policy.
              </p>
            </li>

            <li className="mt-4">
              <span className="font-semibold">SHARING PERSONAL DATA WITH THIRD PARTIES</span>
              <p className="mt-4">
                To operate our Platform and conduct Operations, we may share certain Personal Data that we, or third
                parties acting on our behalf, have collected, with certain third parties. The third parties that we may
                share Personal Data with include:
              </p>

              <ol className={orderedListRoman}>
                <li className="mt-4">
                  Our Operational Service Providers in connection with the services they provide to us, including,
                  without limitation, order fulfillment, marketing, data hosting, payment processing and fraud
                  protection and prevention. We require these Operational Service Providers to exercise reasonable care
                  to protect your Personal Data and restrict their use of your Personal Data to the purposes for which
                  we provided to them. Including, but not limited to, service providers such as Braze, Segment and
                  Bugsnag.
                </li>
                <li className="mt-4">
                  Our Affiliates for purposes related to: (1) our Operations, including, without limitation, to operate
                  our Platform, to process, fulfill or administer orders you place through our Platform and to market or
                  promote our Platform; and (2) their business operations, including, without limitation, marketing and
                  promoting the services they provide. Each Affiliate’s use of your Personal Data is subject to that
                  Affiliate’s privacy policy. In the event a particular Affiliate does not have a privacy policy, unless
                  we inform you otherwise, that Affiliate’s use of your Personal Data will be in accordance with this
                  Policy.
                </li>
                <li className="mt-4">
                  Our Marketing Partners to facilitate the delivery of targeted ads, promotions and offers to you, on
                  behalf of ourselves and our Marketing Partners, on and off our Platform.
                </li>
                <li className="mt-4">
                  In the event of a merger, acquisition, bankruptcy or other sale of all or a portion of our assets, we
                  may transfer or allow third parties to use information owned or controlled by us, including Personal
                  Data. We reserve the right, in connection with these types of transactions, to transfer or assign your
                  Personal Data and other information we, or third parties acting on our behalf, have collected to third
                  parties or to authorize such third parties to use any such information. Except as ordered by a
                  bankruptcy or other court, the use and disclosure of all transferred Personal Data will be subject to
                  this Policy. However, any Personal Data you submit or that is collected after this type of transfer
                  may be subject to a new privacy policy adopted by the successor entity.
                </li>
                <li className="mt-4">
                  Government and law enforcement individuals or private parties where necessary to enforce and comply
                  with the law. To the extent permitted under applicable law, we may disclose any Personal Data and
                  other information to government or law enforcement officials or private parties as we believe is
                  necessary or appropriate to investigate, respond to and defend against legal claims, for legal process
                  (including subpoenas), to protect our property and rights of those of a third party, to protect us
                  against liability, for the safety of the public or any person, to prevent or stop any illegal,
                  unethical, fraudulent, abusive or legally actionable activity, to protect the security and integrity
                  of our Platform and any equipment used to make our Platform available or to comply with applicable
                  laws.
                </li>
                <li className="mt-4">
                  Any third party to whom you consent and/or instruct us to provide your Personal Data. Data obtained
                  through the short code program will not be shared with any third-parties for their marketing
                  reasons/purposes.
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">THIRD PARTY INFORMATION COLLECTION</span>
              <p className="mt-4">
                Based on the nature of our Platform, third parties may collect information from or about you through or
                in connection with our Platform. In addition to other information provided in this Policy, including,
                without limitation, the section titled “How We Collect Usage Data” the categories of third parties that
                may collect information about you through or in connection with our Platform include:
              </p>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  Third parties whose content is linked to on our Platform. Any links to other websites, applications or
                  other third party content on our Platform, are not an endorsement, authorization or representation of
                  our affiliation with that third party and we are not responsible for the content, privacy and security
                  policies and practices of any third party. We encourage you to read the privacy policies or statements
                  applicable to any third party websites, applications or content you access.
                </li>
                <li className="mt-4">
                  Other users of our Platform. Depending on how you interact with our Platform, other users may collect
                  information about you that is published or otherwise generally made available on our Platform. All
                  users of our Platform are subject to our Terms of Use, which are available at{' '}
                  <InternalLink data-testid="terms-of-use-link" className="underline" href={paths.legal.termsOfUse}>
                    {paths.getBaseURL()}
                    {paths.legal.termsOfUse}
                  </InternalLink>
                  . While we place restrictions on what our users are allowed to do with information made available to
                  them on our Platform, we are not responsible for any misuse of any information, including your
                  Personal Data, by any user. If you believe that another user has misused your Personal Data or any
                  other information available on our Platform, please contact us at{' '}
                  <a data-testid="support-link" className="underline" href="mailto:privacy@angel.com">
                    privacy@angel.com
                  </a>
                  .
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">HOW TO MANAGE YOUR PERSONAL DATA</span>
              <p className="mt-4">
                We offer you several choices with respect to how we use your Personal Data. The mechanisms available to
                you to control the Personal Data are:
              </p>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  To update any Personal Data you have provided us, update your preferences or otherwise contact us
                  about our use of your Personal Data, please email us at{' '}
                  <a data-testid="support-link" className="underline" href="mailto:privacy@angel.com">
                    privacy@angel.com
                  </a>
                  .
                </li>
                <li className="mt-4">
                  To manage the communications we send you:
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      For marketing and other promotional email communications you receive from us, you can opt-out of
                      receiving such communications by following the unsubscribe instructions contained in most email
                      messages from us. Your unsubscribe request or email preference changes will be processed promptly,
                      though this process may take several days. During that processing period, you may receive
                      additional marketing and other promotional emails from us. Please note, opting out of these
                      communications will only apply to marketing and other promotional emails and will not apply to any
                      email or other communications we send you for non-marketing purposes, including, but not limited
                      to, emails and other communications about your orders and other interactions with our Platform.
                    </li>
                    <li className="mt-4">
                      If you have provided us with your prior express consent to receive text messages via an automatic
                      telephone system (autodialer), we may use your information to send promotional, personalized
                      advertising and other text messages to any mobile device connected to your mobile phone number.
                      You can opt out from receiving future text messages at any time by: replying STOP to any text
                      messages you receive from us; or sending us an email at{' '}
                      <a data-testid="support-link" className="underline" href="mailto:privacy@angel.com">
                        privacy@angel.com
                      </a>{' '}
                      with the subject line “STOP” and including your phone number in the email. Once you opt out, we
                      will send one additional confirmation text message stating that you have opted out of receiving
                      automated text messages from us. For more information about text messages we may send, please
                      review our Terms of Use, available at{' '}
                      <InternalLink data-testid="terms-of-use-link" className="underline" href={paths.legal.termsOfUse}>
                        {paths.getBaseURL()}
                        {paths.legal.termsOfUse}
                      </InternalLink>
                      .
                    </li>
                  </ol>
                </li>
                <li className="mt-4">
                  If you do not want us to use your personal information to deliver targeted advertisements to you
                  according to our target audience preferences, you can opt out by sending us an email with your request
                  to{' '}
                  <a data-testid="support-link" className="underline" href="mailto:privacy@angel.com">
                    privacy@angel.com
                  </a>
                  . We do not control third parties’ collection or use of your Personal Data to serve interest-based
                  advertising. However, these third parties, which may include our Marketing Partners, may provide you
                  with ways to choose not to have your Personal Data used to deliver targeted to you according to their
                  target audience preferences. You can opt out of receiving targeted ads from members of the Network
                  Advertising Initiative (“NAI”) on the NAI’s website, located at{' '}
                  <ExternalLink
                    data-testid="network-advertising-link"
                    className="underline"
                    href="https://www.networkadvertising.org"
                  >
                    https://www.networkadvertising.org
                  </ExternalLink>
                  , and from members of the Digital Advertising Alliance (“DAA”) on their website, located at{' '}
                  <ExternalLink
                    data-testid="your-ad-choices-link"
                    className="underline"
                    href="https://youradchoices.com"
                  >
                    https://youradchoices.com
                  </ExternalLink>
                  . Please note, even if you opt out from receiving targeted advertisements, you may still see
                  advertisements based on factors such as your general location derived from your IP address, your
                  browser type and your search terms.
                </li>
                <li className="mt-4">
                  Subject to certain exceptions, you can request that we delete any of your Personal Data that we
                  collected and retained through or in connection with certain Third Party Sources and Third Party
                  Single Sign On Providers, including, without limitation, Facebook. Once we receive and confirm your
                  request to delete your applicable Personal Data, we will delete your applicable Personal Data from our
                  records, unless an exception applies. We will also direct our Operational Service Providers to delete
                  your applicable Personal Data that they have retained on our behalf. Please note, we may deny your
                  deletion request under certain circumstances, and will inform you of the basis for the denial, which
                  may include, without limitation, in circumstances where retaining your Personal Information is
                  necessary for us or our service providers to:
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      Complete the transaction for which we collected the Personal Information, provide a good or
                      service that you requested, take actions reasonably anticipated within the context of our ongoing
                      business relationship with you, or otherwise perform our contract with you.
                    </li>
                    <li className="mt-4">
                      Detect security incidents, protect against malicious, deceptive, fraudulent, or illegal activity,
                      or prosecute those responsible for such activities.
                    </li>
                    <li className="mt-4">
                      Enable solely internal uses that are reasonably aligned with consumer expectations based on your
                      relationship with us.
                    </li>
                    <li className="mt-4">Comply with a legal obligation.</li>
                    <li className="mt-4">
                      Make other internal and lawful uses of that Personal Information that are compatible with the
                      context in which you provided it.
                    </li>
                  </ol>
                </li>
                <li className="mt-4">
                  To request deletion of your Personal Information, please submit a request to us by emailing us at
                  <a data-testid="support-link" className="underline" href="mailto:privacy@angel.com">
                    privacy@angel.com
                  </a>
                  . Only you, or a party that you authorized on your behalf, may make a request related to the deletion
                  of your Personal Data. You may also make a request on behalf of your minor child. Your request for
                  deletion must:
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      Provide sufficient information that allows us to reasonably verify you are the person about whom
                      we collected Personal Data or an authorized representative.
                    </li>
                    <li className="mt-4">
                      Describe your request with sufficient detail that allows us to properly understand, evaluate, and
                      respond to it.
                    </li>
                  </ol>
                </li>
                <li className="mt-4">
                  We cannot respond to your request or delete your Personal Data if we cannot verify your identity or
                  authority to make the request and confirm the Personal Information relates to you.
                </li>
                <li className="mt-4">
                  Making a request does not require you to create an account with us and we will only use Personal Data
                  provided in connection with a request for deletion to verify the requestor’s identity or authority to
                  make the request.
                </li>
                <li className="mt-4">
                  We endeavor to respond to deletion requests within a reasonable time period after we receive a
                  deletion request. If we are unable to fully comply with your request to delete your applicable
                  Personal Data, our response will explain the reasons why we are unable to fully fulfill your request.
                </li>
                <li className="mt-4">
                  If we determine that the request warrants a fee, we will tell you why we made that decision and
                  provide you with a cost estimate before completing your request.
                </li>
                <li className="mt-4">
                  Notwithstanding the above, we cannot edit or delete any information that is stored on a blockchain,
                  for example the Ethereum blockchain, as we do not have custody or control over any blockchains. The
                  information stored on the blockchain may include purchases, sales, and transfers related to your
                  blockchain address and NFTs held at that address.
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">PROTECTING YOUR PERSONAL DATA</span>
              <p className="mt-4">
                We are committed to protecting the security of your Personal Data. We maintain commercially reasonable
                safeguards to maintain the security and privacy of Personal Data we collect and use in connection with
                our Platform. Nevertheless, when disclosing Personal Data, you should remain mindful that there is an
                inherent risk in the transmission of information via the internet and mobile platforms. Such risk
                includes but is not limited to profile pictures and stories videos being accessed and “scraped” by third
                party websites and made publicly available on non-Angel websites and databases without our knowledge or
                consent; accordingly, you agree to release and hold Angel harmless for any third-party use of your
                Personal Data (including profile pictures and stories videos). Although we take precautions to protect
                against the risk of unauthorized access to your Personal Data, we cannot guarantee the security of any
                information, including your Personal Data, that you disclose online, and you do so at your own risk.
              </p>
            </li>

            <li className="mt-4">
              <span className="font-semibold">CHILDREN’S PRIVACY</span>
              <p className="mt-4">
                We do not direct or target any piece of our Platform to children under the age of 13. If we learn that
                we have collected the Personal Data of anyone under the age of 13, we will take appropriate steps to
                delete this information. If you are a parent or guardian of someone under the age of 13 and discover
                that your child has submitted Personal Data to us, please contact us at{' '}
                <a data-testid="support-link" className="underline" href="mailto:privacy@angel.com">
                  privacy@angel.com
                </a>
                . We will make reasonable efforts to remove such information from our databases.
              </p>
            </li>

            <li className="mt-4">
              <span className="font-semibold">CALIFORNIA PRIVACY RIGHTS</span>
              <p className="mt-4">
                If you are a California resident, under California’s Shine the Light Law (California Civil Code Section
                1798.83) you have the right to request the following information regarding our disclosure of your
                personal information to third parties for direct marketing purposes: (i) the categories of personal
                information we disclosed to third parties for their direct marketing purposes during the preceding
                calendar year; (ii) the names and addresses of the third parties that received the information; and
                (iii) if the nature of the third party’s business cannot be determined from their name, examples of the
                products or services they marketed. This information may be provided in a standardized form that is not
                specific to you.
              </p>
              <p className="mt-4">
                You are permitted to obtain this information from us once a year, free of charge. To make such a
                request, please submit your request in writing to:
              </p>
              <div className="mt-4">
                <p>Angel Studios Inc.</p>
                <p>Attn: CA Privacy Rights Request</p>
                <p>295 W Center St Suite A</p>
                <p>Provo, UT 84601</p>
              </div>
            </li>

            <li className="mt-4">
              <span className="font-semibold">EUROPEAN USER RIGHTS</span>
              <p className="mt-4">
                If you are located in the EU, Switzerland, or the United Kingdom, you have certain rights with respect
                to your Personal Data. Following is a summary of those rights and additional information applicable to
                our collection and use of your Personal Data.
              </p>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Data Controller</span> When you provide us with your Personal Data in
                  connection with our Platform, we serve as a data controller. When we act as a data controller we
                  determine how your Personal Data will be utilized, in accordance with this Policy.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Legal Basis for Processing</span> We rely on several legal bases to
                  process your Personal Data. These legal bases include where:
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      The processing is necessary to perform our contractual obligations, such as to provide you with
                      access to our Platform;
                    </li>
                    <li className="mt-4">
                      You have given your prior consent, which you may withdraw at any time (such as for marketing
                      purposes or other purposes we obtain your consent for from time to time);
                    </li>
                    <li className="mt-4">
                      The processing is necessary to comply with a legal obligation, a court order, or to exercise or
                      defend legal claims; and
                    </li>
                    <li className="mt-4">
                      The processing is necessary for the purposes of our legitimate interests, such as improving,
                      personalizing, and developing our Platform, marketing new features or products that may be of
                      interest, and promoting safety and security as described above.
                    </li>
                  </ol>
                  <p className="mt-4">
                    If you have any questions about or would like further information about the legal bases we rely on
                    to collect and use your Personal Data, please contact us at{' '}
                    <a data-testid="support-link" className="underline" href="mailto:privacy@angel.com">
                      privacy@angel.com
                    </a>
                    .
                  </p>
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Rights Under the General Data Protection Regulation</span> If you are
                  located in the EU, Switzerland, or the United Kingdom, you have the following rights in respect of
                  your Personal Data that we hold:
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">Right of access. The right to obtain access to your Personal Data.</li>
                    <li className="mt-4">
                      Right to rectification. The right to obtain rectification of your Personal Data without undue
                      delay where that Personal Data is inaccurate or incomplete.
                    </li>
                    <li className="mt-4">
                      Right to erasure. The right to obtain erasure of your Personal Data without undue delay in certain
                      circumstances, such as where the Personal Data is no longer necessary in relation to the purposes
                      for which it was collected or processed.
                    </li>
                    <li className="mt-4">
                      Right to restriction. The right to obtain the restriction of the processing undertaken by us on
                      your Personal Data in certain circumstances, such as where the accuracy of the Personal Data is
                      contested by you, for a period enabling us to verify the accuracy of the Personal Data.
                    </li>
                    <li className="mt-4">
                      Right to portability. The right to portability allows you to move, copy, or transfer Personal Data
                      easily from one organization to another.
                    </li>
                    <li className="mt-4">
                      Right to object. You have a right to object to processing based on legitimate interests and direct
                      marketing.
                    </li>
                  </ol>
                  <p className="mt-4">
                    If you wish to exercise one of these rights, please email us at{' '}
                    <a data-testid="support-link" className="underline" href="mailto:privacy@angel.com">
                      privacy@angel.com
                    </a>
                    . You also have the right to lodge a complaint with your local data protection authority. Further
                    information about how to contact your local data protection authority is available at{' '}
                    <ExternalLink
                      data-testid="euro-privacy-link"
                      className="underline"
                      href="http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm"
                    >
                      http://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm
                    </ExternalLink>
                    .
                  </p>
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Retention of Personal Data</span> We reserve the right to retain any
                  Personal Data as long as it is needed to: (1) fulfill the purposes for which we collected the Personal
                  Data; and (2) comply with applicable law.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Transfers of Personal Data</span> If you are located in the EU,
                  Switzerland, or the United Kingdom, the Personal Data we collect may be stored and processed in any
                  country in which we or our affiliates, suppliers, third party electronic payment processors, and/or
                  agents maintain facilities, including, without limitation, the United States of America. The United
                  States of America has not sought nor received a finding of “adequacy” from the EU, Switzerland, or the
                  United Kingdom. We rely on appropriate safeguards and derogations for specific situations as set forth
                  in applicable laws to protect Personal Data transferred from the EU, Switzerland, or the United
                  Kingdom to third countries and international organizations.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Obligations to Data Protection Authorities (DPAs)</span> We will
                  respond diligently and appropriately to requests from DPAs about this Policy or compliance with
                  applicable data protection privacy laws and regulations. We will, upon request, provide DPAs with
                  names and contact details of the individuals designated to handle this process. With regard to
                  transfers of Personal Data, we will (1) cooperate with inquiries from the DPA responsible for the
                  entity exporting the data and (2) cooperate with its decisions, consistent with applicable law and due
                  process rights. With regard to transfers of data to third parties, we will comply with DPAs decisions
                  relating to it and cooperate with all DPAs in accordance with applicable legislation.
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">INTERNATIONAL TRANSFERS</span>
              <p className="mt-4">
                Personal Data collected in connection with the use and operation of our Platform is stored and processed
                in the United States. If you access, use or otherwise interact with our Platform or otherwise engage
                with us from outside the United States, please be advised that your personal information may be
                initially collected by servers located in the United States or, if initially collected outside the
                United States, transferred to the United States. Please note, the United States has not been assessed as
                having adequate privacy laws by some countries and economic regions.
              </p>
            </li>

            <li className="mt-4">
              <span data-testid="updates-header" className="font-semibold">
                CHANGES AND UPDATES TO THIS POLICY
              </span>
              <p className="mt-4">
                Because the methods used to protect your Personal Data and the services we provide are continually
                evolving, this Policy may change at any time. Unless otherwise noted, those changes will be effective as
                soon as they are posted. If changes are made to this Policy, we will make it known on our Platform or by
                email and will indicate the last date it was updated above. Please check back periodically to see if our
                Policy has been updated.
              </p>
              <p className="mt-4">
                Your continued use of our Platform after any changes to this Policy become effective, constitutes your
                continued acknowledgement that you understand that we may continue to use your Personal Data as
                described in this Policy.
              </p>
            </li>

            <li className="mt-4">
              <span className="font-semibold">CONTACT US</span>
              <p className="mt-4">
                We welcome your questions, comments and concerns about privacy. Please email us at{' '}
                <a data-testid="support-link" className="underline" href="mailto:privacy@angel.com">
                  privacy@angel.com
                </a>{' '}
                or contact us via the{' '}
                <ExternalLink
                  data-testid="contact-us-link"
                  className="underline"
                  href={paths.external.zendesk.contactUs}
                >
                  Contact Us
                </ExternalLink>{' '}
                page on the Angel Studios website with any questions or feedback you have pertaining to our privacy
                practices.
              </p>
            </li>
          </ol>
        </div>
      </PaddedContainer>
    </div>
  )
}
